import React, { useState, useEffect } from 'react'
import {
  Select,
  MenuItem,
  FormControl,
  TextField,
  Box,
  Button,
  Typography,
  SelectChangeEvent,
} from '@mui/material'
import { Add, Remove } from '@mui/icons-material'
import { TNMSelectField } from './TNMSelectField'

const sortData = (data: any, columns: any) => {
  console.log('ERROR sortData ~ data:', data?.costCodes)
  return [...(data?.costCodes || [])].sort((a, b) => {
    const valueA = a[columns[0].field]
    const valueB = b[columns[0].field]
    return valueA.localeCompare(valueB)
  })
}

export const CostCodeSelectField = (props: any) => {
  const {
    setError, // Change this from setErrors to setError
    handleCostCodes,
    costCodeOptions,
    tnmItems = [],
    enableDescription = false,
    initialCostCodes = [
      { costCode: '', percentage: 100, tnmItem: null, description: '' },
    ],
  } = props
  const [costCodes, setCostCodes] = useState(initialCostCodes)
  const [costCodeErrors, setCostCodeErrors] = useState(
    Array.from({ length: initialCostCodes.length }, () => '')
  )

  useEffect(() => {
    handleCostCodes(costCodes)
  }, [costCodes, handleCostCodes])

  const onAddCostCode = () => {
    const newPercentages = parseFloat((100 / (costCodes.length + 1)).toFixed(2))
    const newCostCodes = [
      ...costCodes,
      { costCode: '', percentage: 0, tnmItem: null, description: '' },
    ]
    newCostCodes.forEach(costCode => {
      costCode.percentage = newPercentages
    })
    setCostCodes(newCostCodes)
  }

  const onRemoveCostCode = (index: any) => {
    const newCostCodes = [...costCodes]
    newCostCodes.splice(index, 1)
    const newCostCodeErrors = [...costCodeErrors]
    newCostCodeErrors.splice(index, 1)
    const newPercentages = parseFloat((100 / newCostCodes.length).toFixed(2))
    newCostCodes.forEach(costCode => {
      costCode.percentage = newPercentages
    })
    const isErrors = newCostCodeErrors.some(error => error !== '')
    setError(isErrors ? 'There are errors in the cost codes' : '') // Update this line
    setCostCodes(newCostCodes)
    setCostCodeErrors(newCostCodeErrors)
  }
  const onTnmChange = (event: any, index: any) => {
    const newCostCodes = [...costCodes]
    newCostCodes[index].tnmItem = event.target.value
    setCostCodes(newCostCodes)
  }

  const onCostCodeChange = (event: any, index: any) => {
    const selectedCostCode = event.target.value
    const isExist = costCodes.findIndex(
      (cost: { costCode: any }, idx: number) =>
        idx !== index && cost.costCode === selectedCostCode
    )

    if (isExist !== -1) {
      const newCostCodes = [...costCodes]
      newCostCodes[index].costCode = selectedCostCode
      setCostCodes(newCostCodes)
      const newCostCodeErrors = [...costCodeErrors]
      newCostCodeErrors[index] = 'This cost code has already been chosen.'
      setCostCodeErrors(newCostCodeErrors)
      setError('This cost code has already been chosen.') // Update this line
      return
    }

    const newCostCodeErrors = [...costCodeErrors]
    newCostCodeErrors[index] = ''
    setCostCodeErrors(newCostCodeErrors)

    const newCostCodes = [...costCodes]
    newCostCodes[index].costCode = selectedCostCode
    setCostCodes(newCostCodes)
    const isErrors = newCostCodeErrors.some(error => error !== '')
    setError(isErrors ? 'There are errors in the cost codes' : '') // Update this line
  }

  const onPercentageChange = (event: any, index: any) => {
    const enteredPercentage = parseFloat(event.target.value) || 0

    if (enteredPercentage > 100) {
      return
    }
    const newCostCodes = [...costCodes]
    newCostCodes[index].percentage = parseFloat(event.target.value)
      ? parseFloat(event.target.value)
      : 0
    const totalPercentage = newCostCodes.reduce((total, costCode) => {
      return total + costCode.percentage
    }, 0)
    const indexToUpdate = index + 1 <= newCostCodes.length - 1 ? index + 1 : 0
    if (totalPercentage > 100) {
      const percentDiff = totalPercentage - 100
      if (newCostCodes[indexToUpdate].percentage > percentDiff) {
        newCostCodes[indexToUpdate].percentage = parseFloat(
          (newCostCodes[indexToUpdate].percentage - percentDiff).toFixed(2)
        )
      } else {
        let percentToRemove: number = parseFloat(percentDiff.toFixed(2))
        newCostCodes.forEach((costCode, indx) => {
          if (indx !== index) {
            const removeAmount =
              costCode.percentage - percentToRemove < 0
                ? costCode.percentage
                : percentToRemove
            const newCostCodePercentage = costCode.percentage - removeAmount
            newCostCodes[indx].percentage = parseFloat(
              newCostCodePercentage.toFixed(2)
            )
            percentToRemove = percentToRemove - removeAmount
          }
        })
      }
    } else if (totalPercentage < 100) {
      newCostCodes[indexToUpdate].percentage = parseFloat(
        (
          100 -
          (totalPercentage - newCostCodes[indexToUpdate].percentage)
        ).toFixed(2)
      )
    }
    setCostCodes(newCostCodes)
  }

  const onDescriptionChange = (event: any, index: any) => {
    const descriptionOfWork = event.target.value ? event.target.value : ''
    const newCostCodes = [...costCodes]
    newCostCodes[index].description = descriptionOfWork
    setCostCodes(newCostCodes)
  }

  return (
    <Box
      sx={{ flex: 1, alignItems: 'center', alignContent: 'flex-start', mt: 2 }}>
      <Typography variant='h6'>Cost Codes</Typography>
      {costCodes?.map((costCode: any, key: number) => {
        return (
          <div key={key}>
            <div>
              <FormControl sx={{ m: 1, width: 200 }}>
                <Select
                  name='costcode'
                  variant='filled'
                  value={costCode.costCode}
                  error={costCode.costCode === ''}
                  onChange={e => onCostCodeChange(e, key)}>
                  {costCodeOptions.costCodes?.map(
                    (costCodeOption: any, optionIndex: number) => (
                      <MenuItem key={optionIndex} value={costCodeOption._id}>
                        {costCodeOption.label}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
              <FormControl sx={{ m: 1, width: 100 }}>
                <TextField
                  label='%'
                  name='percentage'
                  variant='filled'
                  value={costCode.percentage}
                  onChange={e => onPercentageChange(e, key)}
                />
              </FormControl>
              <FormControl sx={{ m: 1, width: 200 }}>
                <TNMSelectField
                  options={tnmItems}
                  label='TNM'
                  value={costCode.tnmItem}
                  onChange={(e: SelectChangeEvent) => onTnmChange(e, key)}
                />
              </FormControl>
              <Button
                variant='contained'
                color='secondary'
                sx={{ m: 1 }}
                onClick={onAddCostCode}
                startIcon={<Add />}
                disabled={costCodeErrors[key] ? true : false}>
                Add
              </Button>
            </div>
            <div>
              {enableDescription ? (
                <FormControl sx={{ m: 1, width: '100%' }}>
                  <TextField
                    label='Description of Work'
                    name='description'
                    variant='filled'
                    value={costCode.description}
                    onChange={e => onDescriptionChange(e, key)}
                    multiline
                    rows={2}
                  />
                </FormControl>
              ) : null}
              {costCodes.length > 1 && (
                <Button
                  variant='contained'
                  color='secondary'
                  sx={{ m: 1 }}
                  onClick={() => onRemoveCostCode(key)}
                  startIcon={<Remove />}></Button>
              )}
            </div>
            <p style={{ color: 'red' }}>{costCodeErrors[key]}</p>
          </div>
        )
      })}
    </Box>
  )
}
